import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ALL_AUCTION_TEMPLATES, ON_AUCTION_TEMPLATE_CHANGED } from '../../graphql/auctionTemplates';
import Paginator from '../Paginator';
import {
    parsePaging,
    parseSorting,
} from '../../utils/queryString';
import Spinner from '../Spinner';
import useExtendedQuery from '../../hooks/useExtendedQuery';
import Error from '../errors/Error';
import AuctionTemplatesList from './AuctionTemplatesList';
import CreateAuctionTemplateRow from './CreateAuctionTemplateRow';
import useExtendedSubscription from '../../hooks/useExtendedSubscription';
import SubscriptionActions from '../../utils/SubscriptionActions';
import { showInfoNotification } from '../../actions/notifications';
import Constants from '../../utils/Constants';
import useAuthentication from '../../hooks/useAuthentication';
import { hasPrivilege } from '../../hocs/withPrivileges';
import Privileges from '../../utils/Privileges';

export function createNotificationText(t, action, name, id) {
    let notification;
    switch (action) {
        case SubscriptionActions.AUCTION_TEMPLATE_CREATED:
            notification = t('auction-templates.notification.auction-template-create-thirdparty', { name, id });
            break;
        case SubscriptionActions.AUCTION_TEMPLATE_DELETED:
            notification = t('auction-templates.notification.auction-template-delete-thirdparty', { name, id });
            break;
        default:
            notification = t('auction-templates.notification.auction-templates-modified-thirdparty');
    }
    return notification;
}

function AuctionTemplates() {
    const location = useLocation();
    const { t } = useTranslation();
    const { userPrivileges } = useAuthentication();
    const { page, perPage } = parsePaging(location.search);
    const {
        sortKey = 'name',
        sortDirection = Constants.SORT_ASC,
    } = parseSorting(location.search);
    const dispatch = useDispatch();
    const [refetchProps, setRefetchProps] = useState({
        perPage,
        page,
        sortKey,
        sortDirection,
    });
    const {
        loading,
        error,
        data,
        refetch,
    } = useExtendedQuery(ALL_AUCTION_TEMPLATES, {
        variables: refetchProps,
    });
    const onPageChange = (newPage) => setRefetchProps({
        ...refetchProps,
        page: newPage,
    });
    const onSort = (newSorting) => setRefetchProps({
        ...refetchProps,
        sortKey: newSorting.key,
        sortDirection: newSorting.direction,
    });
    useExtendedSubscription(ON_AUCTION_TEMPLATE_CHANGED, {
        refetch,
        refetchVariables: refetchProps,
        dataProperty: 'auctionTemplateChanged',
        refetchOnOwnResponse: true,
        onChangeCallback: ({ action, model: { name, id } }) => {
            if (hasPrivilege(userPrivileges, [Privileges.AUCTION_TEMPLATE_EDIT])) {
                const notification = createNotificationText(t, action, name, id);
                dispatch(showInfoNotification(notification));
            }
        },
    });
    let result;
    let auctionTemplateProps = null;
    if (loading) {
        result = <Spinner className="auction-templates-loading" />;
    } else if (error) {
        result = <Error className="auction-templates-error" error={error} />;
    } else {
        auctionTemplateProps = {
            availableBiddingTypes: data.allAuctionTemplates.availableBiddingTypes,
            availableAuctionAcceptanceTypes: data.allAuctionTemplates.availableAuctionAcceptanceTypes,
            availableTradePriceCalculationTypes: data.allAuctionTemplates.availableTradePriceCalculationTypes,
            availableAuctionTypes: data.allAuctionTemplates.availableAuctionTypes,
            availableAuctionAlgorithms: data.allAuctionTemplates.availableAuctionAlgorithms,
            availableSettlStartTypes: data.allAuctionTemplates.availableSettlStartTypes,
            availableProducts: data.allAuctionTemplates.availableProducts,
            availableZones: data.allAuctionTemplates.availableZones,
            availableAuctioneers: data.allAuctionTemplates.availableAuctioneers,
            defaultAuctionDurationHours: data.allAuctionTemplates.defaultAuctionDurationHours,
        };
        result = (
            <Paginator
                totalCount={data.allAuctionTemplates.auctionTemplateList.totalCount}
                perPage={perPage}
                currentPage={refetchProps.page}
                onPageChange={onPageChange}
                bottom
            >
                <AuctionTemplatesList
                    auctionTemplates={data.allAuctionTemplates.auctionTemplateList.models}
                    auctionTemplateProps={auctionTemplateProps}
                    onSort={onSort}
                    sortingParam={{ key: refetchProps.sortKey, direction: refetchProps.sortDirection }}
                />
            </Paginator>
        );
    }
    return (
        <>
            <h4>
                <span className="fa-layers fa-fw left-menu--item--icon">
                    <FontAwesomeIcon icon="gavel" transform="shrink-7 right-8 down-2" />
                    <FontAwesomeIcon icon="file-alt" transform="left-4" />
                </span>
                &nbsp;{t('title.auction-templates')}
            </h4>
            {auctionTemplateProps && <CreateAuctionTemplateRow auctionTemplateProps={auctionTemplateProps} />}
            {result}
        </>
    );
}

export default AuctionTemplates;
